import React, { useState } from 'react';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { Modal, PageSteps } from '@common/components';
import { SuppliersManagementInvitationStepSelectSupplier } from './suppliers-management-invitation-step-select-supplier';
import { SuppliersManagementInvitationStepReview } from './suppliers-management-invitation-step-review';
import { SupplierListItemType } from '@institutions/graphql';
import {
  CreateSupplierInvitationMutation,
  CreateSupplierInvitationResponse,
  CreateSupplierInvitationVariables,
} from '@institutions/graphql/mutations/create-supplier-invitation';
import { useMutation, useNavigate } from '@common/hooks';
import { Form } from 'antd';
import { ORGANIZATION_ADMIN_MENU } from 'works/legacy/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

export function SuppliersManagementInvitationSteps() {
  const navigate = useNavigate();
  const [stepTwoForm] = Form.useForm();
  const [selectedSuppliers, setSelectedSuppliers] = useState<
    SupplierListItemType[]
  >([]);

  const [createSupplierInvitationMutation, { loading: confirmLoading }] =
    useMutation<
      CreateSupplierInvitationResponse,
      CreateSupplierInvitationVariables
    >(CreateSupplierInvitationMutation);

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.SUPPLIERS.LABEL,
      path: ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE,
    },
    {
      name: 'Invite Supplier',
      path: `${ORGANIZATION_ADMIN_MENU.SUPPLIERS.ROUTE}/invitation`,
    },
  ]);

  function mergeSelectedSuppliersWithNotes() {
    const invitationNotes = stepTwoForm.getFieldsValue();

    return selectedSuppliers.map((supplier) => ({
      id: supplier.id,
      message: invitationNotes[supplier.id] || '',
    }));
  }

  async function onConfirm() {
    const selectedSuppliersWithNotes = mergeSelectedSuppliersWithNotes();

    const { data, errors } = await createSupplierInvitationMutation({
      variables: { suppliers: selectedSuppliersWithNotes },
    });

    if (errors) {
      createMessage('error', errors.map(({ message }) => message).join(' '));
      return;
    }

    const response = data?.createSupplierInvitation;

    if (response) {
      createMessage('success', 'Suppliers request sent');
      navigate('/works/admin/suppliers');
    }
  }

  return (
    <PageSteps
      confirmLoading={confirmLoading}
      steps={[
        {
          title: 'Select Suppliers',
          page: (
            <SuppliersManagementInvitationStepSelectSupplier
              selectedSuppliers={selectedSuppliers}
              onSelectedSuppliersChange={setSelectedSuppliers}
            />
          ),
          forwardButtonDisabled: selectedSuppliers.length === 0,
        },
        {
          title: 'Invite(s) Summary',
          page: (
            <SuppliersManagementInvitationStepReview
              form={stepTwoForm}
              suppliers={selectedSuppliers}
            />
          ),
        },
      ]}
      onConfirm={() => {
        Modal.confirm({
          title: 'Confirm the request to invite supplier(s)',
          content:
            "Suppliers users will receive email invitations, and have to accept or decline. You'll receive a notification.",
          onOk: onConfirm,
        });
      }}
    />
  );
}
